import React from "react";

import { useIntl } from "react-intl";

import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { LoadingSpinner } from "@mapmycustomers/ui";

import RecapRange, { recapRangeDateFormat } from "@app/enum/preview/RecapRange";
import { formatDate } from "@app/util/formatters";

import styles from "./ActivityChart.module.scss";
import ChartItem from "./ChartItem";
import useActivitiesGroups from "./useActivitiesGroups";
import useXAxisDates from "./useXAxisDates";

interface Props {
  activities: Activity[];
  activityTypes: ActivityType[];
  loading: boolean;
  recapRange: RecapRange;
}

const ActivityChart: React.FC<Props> = ({ activities, activityTypes, loading, recapRange }) => {
  const intl = useIntl();

  const dates: Date[] | undefined = useXAxisDates(recapRange, activities);
  const activityGroups = useActivitiesGroups(activities, activityTypes, dates, recapRange);
  const hasItems = activityGroups?.some(([, groups]) =>
    groups.some(([, activities]) => activities.length)
  );

  return (
    <div className={styles.container}>
      <div className={styles.chartContainer}>
        <div
          className={styles.chart}
          style={{
            gridTemplateColumns: `repeat(${dates?.length ?? 0}, 1fr)`,
            // plus "auto" for dates row
            gridTemplateRows: `1fr auto`,
          }}
        >
          {hasItems ? (
            activityGroups!.map(([date, groups], rowIndex) => (
              <div className={styles.chartColumn} key={date.valueOf()}>
                {groups.some(([, activities]) => activities.length) ? (
                  groups.map(([activityType, activities]) =>
                    activities.length ? (
                      <ChartItem
                        activities={activities}
                        activityType={activityType}
                        className={styles.gridCell}
                        key={activityType.id}
                        rowIndex={rowIndex}
                      />
                    ) : null
                  )
                ) : (
                  // if there's nothing to display in this column, still add an empty gridCell for
                  // correct offset
                  <div className={styles.gridCell} />
                )}
              </div>
            ))
          ) : !dates ? (
            <div className={styles.allTimeRange}>
              {intl.formatMessage({
                id: "preview.activityList.chart.selectTimeRange",
                defaultMessage: "Select a different time range to see activities by type",
                description:
                  "Text when all time range is selected in a chart in Activities section on a Preview pane",
              })}
            </div>
          ) : (
            <div className={styles.noItemsColumn}>
              {intl.formatMessage({
                id: "preview.activityList.chart.noItems",
                defaultMessage: "No activities",
                description:
                  "Text when no filtered activities in a chart in Activities section on a Preview pane",
              })}
            </div>
          )}
          {hasItems &&
            dates!.map((date) => (
              <div className={styles.dateCell} key={date.valueOf()}>
                {formatDate(date, intl.formatMessage(recapRangeDateFormat[recapRange]))}
              </div>
            ))}
        </div>
      </div>
      {loading && (
        <div className={styles.loaderOverlay}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default ActivityChart;
